import { FlexBoxView } from '@play-co/timestep-core/flexbox';
import { ImageScaleView, View, ViewOpts } from '@play-co/timestep-core/ui';
import { Avatar } from 'src/game/components/shared/Avatar';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import {
  TEAM_COMPETITION_REWARDS,
  TeamCompetitor,
} from 'src/replicant/team/competition/teamCompetition.ruleset';
import { Match2RewardItem } from '../../components/Match2RewardItem';

export class TeamCompetitorEntry extends View {
  public readonly bg: ImageScaleView;
  private readonly position: { update: (team: TeamCompetitor) => void };
  private readonly avatar: Avatar;
  private readonly name: { update: (team: TeamCompetitor) => void };
  private readonly level: { update: (team: TeamCompetitor) => void };
  private readonly reward: { update: (team: TeamCompetitor) => void };

  constructor(opts: ViewOpts) {
    super({ width: 553, height: 75, ...opts });

    this.bg = new ImageScaleView({
      superview: this,
      width: this.style.width,
      height: this.style.height,
      image: 'assets/ui/match2/common/list_entry.png',
      scaleMethod: '9slice',
      sourceSlices: {
        horizontal: { left: 22, right: 22 },
        vertical: { top: 22, bottom: 22 },
      },
    });

    const layout = new FlexBoxView({
      superview: this,
      width: this.style.width,
      height: this.style.height,
      flex: {
        flexDirection: 'row',
        justifyContent: 'flex-start',
        alignItems: 'center',
      },
    });

    this.position = this.createPositionView(layout);

    this.avatar = new Avatar({
      superview: layout,
      width: 54,
      height: 54,
      centerOnOrigin: false,
    });

    this.avatar.updateOpts({
      offsetY: -3,
      offsetX: 7,
    });

    this.name = this.createNameView(layout);
    this.reward = this.createRewardView(layout);
    this.level = this.createScoreView(layout);
  }

  private createPositionView(superview: View) {
    const layout = new FlexBoxView({
      superview,
      width: 80,
      height: this.style.height,
      flex: {
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'row',
      },
    });

    const text = new LangBitmapFontTextView({
      superview: layout,
      align: 'center',
      verticalAlign: 'center',
      size: 34,
      font: 'Title',
      width: 65,
      height: 61,
    });

    const icon = new ImageScaleView({
      superview: layout,
      scaleMethod: 'contain',
      align: 'center',
      verticalAlign: 'center',
      width: 65,
      height: 61,
    });

    return {
      update: (team: TeamCompetitor) => {
        if (team.position >= 1 && team.position <= 3) {
          icon.updateOpts({
            image: `assets/ui/match2/tournament/star_tournament_medal_${team.position}.png`,
            visible: true,
          });
          text.updateOpts({ visible: false });
        } else {
          text.updateOpts({ visible: true });
          icon.updateOpts({ visible: false });
          text.localeText = () => team.position.toString();
        }
      },
    };
  }

  private createNameView(superview: View) {
    const layout = new FlexBoxView({
      superview,
      width: 130,
      height: this.style.height,
      flex: {
        top: -3,
        marginLeft: 10,
        flexDirection: 'row',
        justifyContent: 'flex-start',
        alignItems: 'center',
      },
    });

    const name = new LangBitmapFontTextView({
      superview: layout,
      align: 'left',
      verticalAlign: 'center',
      size: 28,
      font: 'Body',
      width: 280,
      height: this.style.height,
      color: '#662420',
    });

    return {
      update: (team: TeamCompetitor) => {
        name.localeText = () => team.profile.name;

        name.updateOpts(
          team.current
            ? {
                font: 'Title',
                color: '#ffffff',
              }
            : {
                font: 'Body',
                color: '#78263E',
              },
        );
      },
    };
  }

  private createRewardView(superview: View) {
    const parent = new View({
      superview,
      width: superview.style.height + 50,
      height: superview.style.height,
    });

    const rewardsView = new Match2RewardItem({
      superview: parent,
      size: superview.style.height,
      scale: 0.7,
      labelOffsetY: -10,
      x: 30,
      y: 5,
    });

    return {
      update: (team: TeamCompetitor) => {
        const rewards =
          'levelRequired' in team.profile
            ? TEAM_COMPETITION_REWARDS[team.position - 1]
            : undefined;
        rewardsView.setReward(rewards);
      },
    };
  }

  private createScoreView(superview: View) {
    const layout = new FlexBoxView({
      superview,
      width: 105,
      height: superview.style.height,
      flex: {
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        alignSelf: 'flex-end',
      },
    });

    const bg = new ImageScaleView({
      superview: layout,
      width: 117,
      height: 42,
      image: 'assets/ui/match2/team/competition/comp_score_frame.png',
    });

    new ImageScaleView({
      superview: bg,
      align: 'center',
      verticalAlign: 'center',
      width: 50,
      height: 50,
      image: 'assets/ui/match2/team/competition/comp_token.png',
      centerOnOrigin: true,
      y: bg.style.height * 0.5,
    });

    const level = new LangBitmapFontTextView({
      superview: bg,
      align: 'center',
      verticalAlign: 'center',
      size: 28,
      font: 'Title',
      width: bg.style.width,
      height: bg.style.height - 2,
      color: '#ffffff',
      localeText: () => `0`,
    });

    return {
      update: (team: TeamCompetitor) => {
        level.localeText = () => team.score.toString();
      },
    };
  }

  public setup(params: TeamCompetitor & { alt?: true }) {
    this.position.update(params);
    this.level.update(params);
    this.reward.update(params);
    this.name.update(params);

    if ('icon' in params.profile) {
      this.avatar.updateImage(params.profile.icon);
    } else {
      this.avatar.updateImage(params.profile.avatar);
    }

    if ('frame' in params.profile) {
      this.avatar.setFrame(params.profile.frame);
    }

    if (params.alt) {
      this.bg.updateOpts({
        offsetY: -7,
        image: 'assets/ui/match2/team/frames/comp_sticky_panel.png',
      });

      return;
    }
    this.bg.updateOpts({
      image: params.current
        ? 'assets/ui/match2/team/frames/comp_panel_me.png'
        : 'assets/ui/match2/common/list_entry.png',
    });
  }
}
